<template>
  <div>
    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="info" />
    </div>
    <b-alert v-else-if="loadError" show variant="danger"
      >{{ $t("label.eventLoadError") }}
      <b-button variant="outline-danger" size="sm" @click="loadEvent">{{
        $t("label.retryPermissionDefinitions")
      }}</b-button></b-alert
    >
    <form v-else @submit.prevent="submitForm">
      <b-alert v-if="validationError" show variant="danger">{{
        $t(validationError)
      }}</b-alert>
      <fieldset :disabled="disabled">
        <b-form-group :label="$t('label.name')"
          ><b-form-input v-model.trim="form.name" required
        /></b-form-group>
        <b-form-group :label="$t('label.description')"
          ><b-form-textarea v-model="form.description" rows="4" required
        /></b-form-group>
        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('label.eventWhoCanJoin')">
              <multiselect
                v-model="form.company"
                :placeholder="$t('label.eventAllCompanies')"
                :options="companies"
                track-by="value"
                label="text"
                :disabled="disabled"
                :internal-search="false"
                :loading="companyLoading"
                :allow-empty="true"
                @search-change="searchCompanies"
              />
              <b-form-text>{{ $t("label.eventCompanyHint") }}</b-form-text>
              <b-form-text v-if="companyError" class="text-danger"
                >{{ $t("label.eventCompanyError") }}
                <b-button
                  type="button"
                  size="sm"
                  variant="link"
                  @click="loadCompanies()"
                  >{{ $t("label.retryPermissionDefinitions") }}</b-button
                ></b-form-text
              >
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group :label="$t('label.eventPicUser')">
              <multiselect
                v-model="form.picUser"
                :options="users"
                track-by="value"
                label="text"
                :disabled="disabled"
                :internal-search="false"
                :loading="userLoading"
                :allow-empty="false"
                @search-change="searchUsers"
              />
              <b-form-text v-if="userError" class="text-danger"
                >{{ $t("label.eventUserError") }}
                <b-button
                  type="button"
                  size="sm"
                  variant="link"
                  @click="loadUsers()"
                  >{{ $t("label.retryPermissionDefinitions") }}</b-button
                ></b-form-text
              >
            </b-form-group>
          </div>
        </div>
        <b-form-group :label="$t('label.eventKeywords')"
          ><b-form-input v-model="form.keywordsText" /><b-form-text>{{
            $t("label.eventKeywordsHint")
          }}</b-form-text></b-form-group
        >
        <b-form-group :label="$t('label.eventCoverImage')">
          <img
            v-if="previewURL || existingCover"
            :src="previewURL || existingCover"
            :alt="form.name"
            class="event-cover-preview rounded border mb-2"
          />
          <b-form-file
            v-if="!readOnly"
            v-model="coverFile"
            accept="image/jpeg,image/png,image/gif,image/webp"
            :disabled="disabled"
          />
        </b-form-group>
        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('label.eventVenueType')"
              ><b-form-select
                v-model="form.venueType"
                :options="venueOptions"
                required
            /></b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group :label="$t('label.eventVenueName')"
              ><b-form-input v-model.trim="form.venueName" required
            /></b-form-group>
          </div>
        </div>
        <b-form-group :label="$t('label.eventVenueLink')"
          ><b-form-input
            v-model.trim="form.venueLink"
            type="url"
            :required="venueLinkRequired"
        /></b-form-group>
        <b-form-group :label="$t('label.eventLink')"
          ><b-form-input v-model.trim="form.link" type="url"
        /></b-form-group>
        <p class="small text-muted">{{ $t("label.eventTimezone") }}</p>
        <div class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('label.eventStartedAt')"
              ><b-form-input
                v-model="form.startedAt"
                type="datetime-local"
                required
            /></b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group :label="$t('label.eventEndedAt')"
              ><b-form-input
                v-model="form.endedAt"
                type="datetime-local"
                required
            /></b-form-group>
          </div>
        </div>
        <b-form-checkbox
          v-model="form.registrationEnabled"
          switch
          class="mb-3"
          >{{ $t("label.eventRegistrationEnabled") }}</b-form-checkbox
        >
        <div v-if="form.registrationEnabled" class="row">
          <div class="col-md-6">
            <b-form-group :label="$t('label.eventRegistrationClosedAt')"
              ><b-form-input
                v-model="form.registrationClosedAt"
                type="datetime-local"
                required
            /></b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group :label="$t('label.eventCapacity')"
              ><b-form-input
                v-model="form.capacity"
                type="number"
                min="0"
                step="1"
            /></b-form-group>
          </div>
        </div>
      </fieldset>
      <p v-if="eventUUID" class="text-muted">
        {{ $t("label.eventJoinedCount") }}: {{ joinedCount
        }}<span v-if="eventStatus"> · {{ eventStatus }}</span>
      </p>
      <hr />
      <div class="d-flex justify-content-end">
        <b-button
          type="button"
          variant="outline-secondary"
          class="mr-2"
          :disabled="!status.save"
          @click="$emit('done')"
          >{{ $t("label.close") }}</b-button
        >
        <b-button
          v-if="!readOnly && canWrite"
          type="submit"
          variant="info"
          :disabled="!status.save"
          ><b-spinner v-if="!status.save" small /><span v-else>{{
            $t(eventUUID ? "label.updateX" : "label.createX", [
              $t("module.event"),
            ])
          }}</span></b-button
        >
      </div>
    </form>
  </div>
</template>
<script>
import { mapState, mapActions } from "vuex";
import Multiselect from "vue-multiselect";
import moment from "moment";
import { lookupService } from "@/services/api/lookup.service";
import { eventDateInput } from "@/models/api/event.model";
import { sessionHelper } from "@/utils";
const emptyForm = () => ({
  name: "",
  description: "",
  company: null,
  picUser: null,
  keywordsText: "",
  venueType: 2,
  venueName: "",
  venueLink: "",
  link: "",
  startedAt: "",
  endedAt: "",
  registrationEnabled: false,
  registrationClosedAt: "",
  capacity: "",
});
const imageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
export default {
  name: "EventForm",
  components: { Multiselect },
  props: {
    eventUUID: { type: String, default: "" },
    readOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      form: emptyForm(),
      loading: true,
      loadError: false,
      validationError: "",
      existingCover: "",
      coverFile: null,
      previewURL: "",
      joinedCount: 0,
      eventStatus: "",
      companies: [],
      users: [],
      companyLoading: false,
      userLoading: false,
      companyError: false,
      userError: false,
      companyTimer: null,
      userTimer: null,
      companyRequest: 0,
      userRequest: 0,
      loadRequest: 0,
    };
  },
  computed: {
    ...mapState("api/event", ["status", "details"]),
    canWrite() {
      const user = this.$store.state.api.auth.user?.user;
      return (
        sessionHelper.getUserPermissions(user?.permissions, user?.isSuperadmin)
          .event_admin === 2
      );
    },
    disabled() {
      return this.readOnly || !this.canWrite || !this.status.save;
    },
    venueLinkRequired() {
      return [0, 1].includes(this.form.venueType);
    },
    venueOptions() {
      return [
        { value: 0, text: this.$t("label.eventVirtual") },
        { value: 1, text: this.$t("label.eventHybrid") },
        { value: 2, text: this.$t("label.eventPhysical") },
      ];
    },
  },
  watch: {
    coverFile(file) {
      if (this.previewURL) URL.revokeObjectURL(this.previewURL);
      this.previewURL =
        file && imageTypes.includes(file.type) ? URL.createObjectURL(file) : "";
    },
  },
  created() {
    this.loadEvent();
    if (!this.readOnly && this.canWrite) {
      this.loadCompanies();
      this.loadUsers();
    }
  },
  beforeDestroy() {
    clearTimeout(this.companyTimer);
    clearTimeout(this.userTimer);
    this.companyRequest++;
    this.userRequest++;
    this.loadRequest++;
    if (this.previewURL) URL.revokeObjectURL(this.previewURL);
  },
  methods: {
    ...mapActions("api/event", ["getEvent", "createEvent", "updateEvent"]),
    async loadEvent() {
      const request = ++this.loadRequest;
      this.loading = true;
      this.loadError = false;
      try {
        if (this.eventUUID) {
          const result = await this.getEvent({ uuid: this.eventUUID });
          if (request !== this.loadRequest) return;
          if (!result?.success) {
            this.loadError = true;
            return;
          }
          const event = this.details;
          this.form = {
            name: event.name,
            description: event.description,
            company: event.company,
            picUser: event.picUser,
            keywordsText: event.keywords.join(", "),
            venueType: event.venueType,
            venueName: event.venueName,
            venueLink: event.venueLink,
            link: event.link,
            startedAt: eventDateInput(event.startedAt),
            endedAt: eventDateInput(event.endedAt),
            registrationEnabled: event.registrationEnabled,
            registrationClosedAt: eventDateInput(event.registrationClosedAt),
            capacity: event.capacity,
          };
          this.existingCover = event.coverImage;
          this.joinedCount = event.joinedCount;
          this.eventStatus = event.eventStatus;
        }
      } catch (error) {
        if (request === this.loadRequest) this.loadError = true;
      } finally {
        if (request === this.loadRequest) this.loading = false;
      }
    },
    lookupRows(result) {
      const rows = Array.isArray(result?.data)
        ? result.data
        : result?.data?.data;
      if (!result?.success || !Array.isArray(rows))
        throw new Error("Lookup failed.");
      return rows;
    },
    async loadCompanies(term = "") {
      if (
        this.readOnly ||
        sessionHelper.getPermissionLevel("event_admin") !== 2
      )
        return;
      const request = ++this.companyRequest;
      this.companyLoading = true;
      this.companyError = false;
      try {
        const rows = this.lookupRows(
          await lookupService.getCompanies(term ? [term] : []),
        );
        if (request === this.companyRequest)
          this.companies = rows
            .filter((item) => item.uuid)
            .map((item) => ({
              value: item.uuid,
              text: item.name || item.uuid,
            }));
      } catch (error) {
        if (request === this.companyRequest) this.companyError = true;
      } finally {
        if (request === this.companyRequest) this.companyLoading = false;
      }
    },
    searchCompanies(term) {
      clearTimeout(this.companyTimer);
      this.companyTimer = setTimeout(() => this.loadCompanies(term), 350);
    },
    async loadUsers(term = "") {
      if (
        this.readOnly ||
        sessionHelper.getPermissionLevel("event_admin") !== 2
      )
        return;
      const request = ++this.userRequest;
      this.userLoading = true;
      this.userError = false;
      try {
        const rows = this.lookupRows(
          await lookupService.getUsers({ searchWords: term ? [term] : [] }),
        );
        if (request === this.userRequest)
          this.users = rows
            .filter((item) => item.uuid)
            .map((item) => ({
              value: item.uuid,
              text:
                item.name ||
                item.full_name ||
                [item.first_name, item.last_name].filter(Boolean).join(" ") ||
                item.email ||
                item.uuid,
            }));
      } catch (error) {
        if (request === this.userRequest) this.userError = true;
      } finally {
        if (request === this.userRequest) this.userLoading = false;
      }
    },
    searchUsers(term) {
      clearTimeout(this.userTimer);
      this.userTimer = setTimeout(() => this.loadUsers(term), 350);
    },
    validateForm() {
      if (
        !this.form.name.trim() ||
        !this.form.description.trim() ||
        !this.form.picUser ||
        ![0, 1, 2].includes(this.form.venueType)
      )
        return "label.eventRequiredFields";
      if (!this.form.venueName.trim()) return "label.eventVenueNameRequired";
      if (this.venueLinkRequired && !this.form.venueLink.trim())
        return "label.eventVenueLinkRequired";
      const started = moment.utc(this.form.startedAt, "YYYY-MM-DDTHH:mm", true),
        ended = moment.utc(this.form.endedAt, "YYYY-MM-DDTHH:mm", true);
      if (!started.isValid() || !ended.isValid() || !ended.isAfter(started))
        return "label.eventDateError";
      if (
        this.form.registrationEnabled &&
        !moment
          .utc(this.form.registrationClosedAt, "YYYY-MM-DDTHH:mm", true)
          .isValid()
      )
        return "label.eventRegistrationDateError";
      if (
        this.form.capacity !== "" &&
        this.form.capacity != null &&
        (!Number.isSafeInteger(Number(this.form.capacity)) ||
          Number(this.form.capacity) < 0)
      )
        return "label.eventCapacityError";
      if (this.coverFile && !imageTypes.includes(this.coverFile.type))
        return "label.eventImageError";
      return "";
    },
    payload() {
      return {
        uuid: this.eventUUID,
        company_uuid: this.form.company?.value || null,
        pic_user_uuid: this.form.picUser.value,
        name: this.form.name.trim(),
        description: this.form.description.trim(),
        cover_image: this.coverFile,
        keywords: [
          ...new Set(
            this.form.keywordsText
              .split(",")
              .map((keyword) => keyword.trim())
              .filter(Boolean),
          ),
        ],
        venue_type: this.form.venueType,
        venue_display_name: this.form.venueName,
        venue_link: this.form.venueLink,
        link: this.form.link,
        started_at: this.form.startedAt.replace("T", " "),
        ended_at: this.form.endedAt.replace("T", " "),
        is_registration_enabled: this.form.registrationEnabled,
        registration_closed_at: this.form.registrationClosedAt.replace(
          "T",
          " ",
        ),
        capacity: this.form.capacity,
      };
    },
    async submitForm() {
      if (
        this.disabled ||
        sessionHelper.getPermissionLevel("event_admin") !== 2
      )
        return;
      this.validationError = this.validateForm();
      if (this.validationError) return;
      const result = await this[this.eventUUID ? "updateEvent" : "createEvent"](
        { data: this.payload() },
      );
      if (result?.success) this.$emit("saved");
    },
  },
};
</script>
<style scoped>
.event-cover-preview {
  display: block;
  width: 160px;
  height: 110px;
  object-fit: contain;
}
</style>
